"use client";

import { FetchPill } from "@/fetch-ui/FetchPill";
import { Check, Plus, X } from "lucide-react";

interface SuggestionPillProps {
  label: string;
  /** Whether this suggestion is currently one of the event's locations. */
  active: boolean;
  /** Whether the location limit blocks adding it (rendered dimmed, still clickable to explain why). */
  limitBlocked: boolean;
  onClick: () => void;
}

/**
 * A one-click location suggestion on the location step: "+" to add, a check once added, and an ×
 * on hover to signal that clicking again removes it.
 *
 * @param props - The label, selection state, and click handler.
 * @returns The pill button.
 */
export const SuggestionPill = ({ label, active, limitBlocked, onClick }: SuggestionPillProps) => (
  <FetchPill
    type="button"
    size="sm"
    selected={active}
    aria-disabled={limitBlocked}
    onClick={onClick}
    className={`group gap-1.5 px-3 py-1 font-medium transition-[background-color,transform] duration-150 active:scale-[0.97] motion-reduce:transition-colors motion-reduce:active:scale-100 ${
      limitBlocked ? "opacity-50" : ""
    }`}
  >
    {active ? (
      <>
        {/* Selected shows a check; hovering reveals an × to signal "click to remove". */}
        <Check className="-ml-0.5 h-3.5 w-3.5 group-hover:hidden" aria-hidden="true" />
        <X className="-ml-0.5 hidden h-3.5 w-3.5 group-hover:block" aria-hidden="true" />
      </>
    ) : (
      <Plus className="-ml-0.5 h-3.5 w-3.5 opacity-60" aria-hidden="true" />
    )}
    {label}
  </FetchPill>
);
